import classNames from 'classnames'
import { Fragment, type FC } from 'react'
import { Button } from '../../../../components/Button/Button'
import { GroupCheckbox } from '../../../../components/GroupCheckbox/GroupCheckbox'
import { PiiShieldEntityRow } from './PiiShieldEntityRow'
import { type PiiCategoryDefinition, type PiiEntity } from '../../../../common/piiShieldCategories'

interface PiiShieldCategoryCardProps {
  category: PiiCategoryDefinition
  redactedEntities: Set<PiiEntity>
  isEditMode: boolean
  isExpanded: boolean
  onToggleExpand: (categoryId: string) => void
  onToggleEntity: (entity: PiiEntity) => void
  onToggleCategory: (entities: PiiEntity[], allRedacted: boolean) => void
}

export const PiiShieldCategoryCard: FC<PiiShieldCategoryCardProps> = ({
  category,
  redactedEntities,
  isEditMode,
  isExpanded,
  onToggleExpand,
  onToggleEntity,
  onToggleCategory,
}) => {
  const entityValues = category.entities.map((entity) => entity.value)
  const redactedInCategory = entityValues.filter((value) => redactedEntities.has(value))

  return (
    <div className='rounded-3 border zocks-border-color-gray-300 zocks-bg-color-white mb-3'>
      <div className='d-flex align-items-center justify-content-between zocks-gap-2 py-3 px-4'>
        <div className='d-flex align-items-start zocks-gap-0.5'>
          {isEditMode ? (
            <div className='pii-shield-checkbox'>
              <GroupCheckbox
                options={entityValues}
                selectedOptions={redactedInCategory}
                onToggle={(options, allEnabled) => onToggleCategory(options as PiiEntity[], allEnabled)}
              />
            </div>
          ) : null}
          <Button
            className='d-flex flex-column align-items-start text-start p-0 zocks-gap-0.25'
            onClick={() => onToggleExpand(category.id)}
            aria-expanded={isExpanded}
          >
            <span className='paragraph-medium fw-bold zocks-text-gray-900'>{category.label}</span>
            <span className='label-medium'>
              {category.entities.map((entity, index) => (
                <Fragment key={entity.value}>
                  {index > 0 ? <span className='zocks-text-gray-500'>, </span> : null}
                  <span className={redactedEntities.has(entity.value) ? 'zocks-text-primary-dark' : 'zocks-text-gray-500'}>{entity.label}</span>
                </Fragment>
              ))}
            </span>
          </Button>
        </div>
        <Button className='p-0' onClick={() => onToggleExpand(category.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.label}`}>
          <i className={classNames('bi zocks-text-gray-600', isExpanded ? 'bi-chevron-contract' : 'bi-chevron-expand')} />
        </Button>
      </div>

      {isExpanded
        ? category.entities.map((entity, index) => (
            <PiiShieldEntityRow
              key={entity.value}
              entity={entity}
              isEditMode={isEditMode}
              isRedacted={redactedEntities.has(entity.value)}
              isFirst={index === 0}
              onToggle={onToggleEntity}
            />
          ))
        : null}
    </div>
  )
}
